<template>
  <v-app-bar class="control-header" color="white" elevation="1" height="52">
    <v-btn
      v-if="!['hr', 'accountant'].includes(user.role)"
      class="control-header__sidebar-toggle"
      icon="mdi-menu"
      variant="text"
      aria-label="Open main navigation"
      @click="$emit('toggle-sidebar')"
    ></v-btn>

    <div class="control-header__brand">
      <img class="control-header__logo" src="/favicon.png" alt="" />
      <div class="control-header__title">MODATO</div>
    </div>

    <div class="control-header__divider" aria-hidden="true"></div>

    <nav class="control-header__navigation" aria-label="Control Operativo">
      <button
        v-for="item in visibleNavigationItems"
        :key="item.key"
        :class="['control-header__item', { 'control-header__item--active': item.route === $route.path }]"
        type="button"
        @click="selectItem(item)"
      >
        <span>{{ item.label }}</span>
        <span
          v-if="item.key === 'card-expenses' && currentStudioPendingCount > 0"
          class="control-header__pending-badge"
        >{{ badgeCount(currentStudioPendingCount) }}</span>
      </button>
    </nav>

    <v-spacer></v-spacer>

    <div v-if="canSwitchStudio" class="control-header__studio-switcher">
      <span class="control-header__studio-label">Studio</span>
      <div class="control-header__studio-select-wrap">
        <v-select
          v-model="selectedStudioId"
          class="control-header__studio-select"
          :items="studios"
          item-title="name"
          item-value="id"
          density="compact"
          variant="outlined"
          hide-details
          :loading="loadingStudios"
          aria-label="Active studio"
          @update:model-value="switchStudio"
        >
          <template #item="{ props, item }">
            <v-list-item v-bind="props" :title="item.raw.name">
              <template #append>
                <div class="control-header__studio-alerts">
                  <span
                    v-if="pendingForStudio(item.raw.id) > 0"
                    class="control-header__pending-badge control-header__pending-badge--menu"
                    :title="`${pendingForStudio(item.raw.id)} pending Card Expenses`"
                  >{{ badgeCount(pendingForStudio(item.raw.id)) }}</span>
                  <span
                    v-if="unlinkedBanksForStudio(item.raw.id) > 0"
                    class="control-header__bank-badge control-header__bank-badge--menu"
                    :title="`${unlinkedBanksForStudio(item.raw.id)} active models need bank linking or an update`"
                  >
                    <v-icon size="14">mdi-bank-outline</v-icon>
                    {{ badgeCount(unlinkedBanksForStudio(item.raw.id)) }}
                  </span>
                </div>
              </template>
            </v-list-item>
          </template>
        </v-select>
        <span
          v-if="otherStudiosPendingCount > 0"
          class="control-header__selector-alert"
          :title="`${otherStudiosPendingCount} pending in other studios`"
        >{{ badgeCount(otherStudiosPendingCount) }}</span>
        <span
          v-if="totalUnlinkedBankCount > 0"
          class="control-header__bank-badge control-header__selector-bank-alert"
          :title="`${totalUnlinkedBankCount} active models need bank linking or an update`"
        >
          <v-icon size="14">mdi-bank-outline</v-icon>
          {{ badgeCount(totalUnlinkedBankCount) }}
        </span>
      </div>
    </div>

    <v-btn
      v-if="['hr', 'accountant'].includes(user.role)"
      icon="mdi-logout"
      variant="text"
      title="Log out"
      aria-label="Log out"
      @click="logout"
    />

    <v-menu location="bottom end">
      <template #activator="{ props }">
        <v-btn
          v-bind="props"
          class="control-header__mobile-menu"
          icon="mdi-dots-vertical"
          variant="text"
          aria-label="Open Control Operativo navigation"
        ></v-btn>
      </template>

      <v-list density="compact" min-width="230">
        <v-list-subheader>CONTROL OPERATIVO</v-list-subheader>
        <v-list-item
          v-for="item in visibleNavigationItems"
          :key="item.key"
          @click="selectItem(item)"
        >
          <v-list-item-title>{{ item.label }}</v-list-item-title>
          <template #append>
            <span
              v-if="item.key === 'card-expenses' && currentStudioPendingCount > 0"
              class="control-header__pending-badge control-header__pending-badge--menu"
            >{{ badgeCount(currentStudioPendingCount) }}</span>
          </template>
        </v-list-item>
      </v-list>
    </v-menu>

    <v-snackbar v-model="showPlannedMessage" location="top" :timeout="2400">
      <strong>{{ selectedLabel }}</strong> is planned and will be connected when its page is built.
      <template #actions>
        <v-btn variant="text" @click="showPlannedMessage = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-app-bar>
</template>

<script>
import secureAxios from '../utils/secureAxios';

export default {
  name: 'HeaderBar',
  emits: ['toggle-sidebar'],
  data() {
    return {
      selectedLabel: '',
      showPlannedMessage: false,
      user: this.readStoredUser(),
      studios: [],
      selectedStudioId: null,
      loadingStudios: false,
      pendingCountsByStudio: {},
      unlinkedBankCountsByStudio: {},
      pendingRefreshTimer: null,
      navigationItems: [
        { key: 'model-schedule', label: 'Model Schedule', route: '/model-schedule' },
        { key: 'admin-schedule', label: 'Admin Schedule', route: '/admin-schedule' },
        { key: 'weekly-sales', label: 'Weekly Sales', route: '/weekly-sales' },
        { key: 'card-expenses', label: 'Card Expenses', route: '/card-expenses' },
        { key: 'cash-box', label: 'Cash Box', route: '/cash-box' },
        { key: 'inventory', label: 'Inventory', route: '/inventory' },
        { key: 'calendar', label: 'Calendar', route: '/calendar' },
        { key: 'periods', label: 'Periods', route: '/periods' },
      ],
    };
  },
  computed: {
    canSwitchStudio() {
      const hasPermission = this.user.can_switch_studio === true
        || Number(this.user.can_switch_studio) === 1;
      return ((this.user.role === 'admin'
        && ['carolina.r@mgrouplcs.com', 'jono@mgrouplcs.com', 'jorge.n@mgrouplcs.com'].includes(String(this.user.email || '').trim().toLowerCase())
        || this.user.role === 'accountant'
        || this.user.role === 'support'))
        && hasPermission;
    },
    currentStudioPendingCount() {
      return this.pendingForStudio(this.selectedStudioId || this.user.studio_id);
    },
    totalPendingCount() {
      return Object.values(this.pendingCountsByStudio)
        .reduce((total, count) => total + Number(count || 0), 0);
    },
    otherStudiosPendingCount() {
      return Math.max(0, this.totalPendingCount - this.currentStudioPendingCount);
    },
    totalUnlinkedBankCount() {
      return Object.values(this.unlinkedBankCountsByStudio)
        .reduce((total, count) => total + Number(count || 0), 0);
    },
    visibleNavigationItems() {
      const user = this.user;
      if (user.role === 'hr') {
        return this.navigationItems.filter((item) => ['model-schedule', 'admin-schedule', 'calendar'].includes(item.key));
      }
      if (user.role === 'accountant') {
        return this.navigationItems.filter((item) => ['card-expenses', 'cash-box', 'calendar', 'periods'].includes(item.key));
      }
      if (user.role === 'support') {
        return this.navigationItems.filter((item) => ['model-schedule', 'admin-schedule', 'calendar', 'periods'].includes(item.key));
      }
      const canAccessAdminSchedule = user.role === 'admin'
        || user.isAdmin === true
        || user.role === 'satellite_monitor'
        || ['carolina.r@mgrouplcs.com', 'jono@mgrouplcs.com', 'jorge.n@mgrouplcs.com'].includes(String(user.email || '').trim().toLowerCase());
      return this.navigationItems.filter((item) => item.key !== 'admin-schedule' || canAccessAdminSchedule);
    },
  },
  mounted() {
    if (this.user.role === 'hr') return;
    if (this.canSwitchStudio) this.loadStudios();
    else this.selectedStudioId = Number(this.user.studio_id) || null;
    if (this.user.role === 'support') return;
    this.loadPendingCounts();
    this.loadUnlinkedBankCounts();
    window.addEventListener('card-expenses-updated', this.loadPendingCounts);
    window.addEventListener('model-bank-link-updated', this.loadUnlinkedBankCounts);
    this.pendingRefreshTimer = window.setInterval(this.refreshHeaderAlerts, 60000);
  },
  beforeUnmount() {
    window.removeEventListener('card-expenses-updated', this.loadPendingCounts);
    window.removeEventListener('model-bank-link-updated', this.loadUnlinkedBankCounts);
    window.clearInterval(this.pendingRefreshTimer);
  },
  methods: {
    readStoredUser() {
      try {
        return JSON.parse(localStorage.getItem('user') || '{}');
      } catch {
        return {};
      }
    },
    async loadStudios() {
      this.loadingStudios = true;
      try {
        const studioEndpoint = this.user.role === 'support'
          ? '/api/model-schedule/studios'
          : '/api/card-expenses/studios';
        const { data } = await secureAxios.get(studioEndpoint);
        this.studios = (data || []).map((studio) => ({
          ...studio,
          id: Number(studio.id),
        }));
        const currentStudioId = Number(this.user.studio_id);
        this.selectedStudioId = this.studios.some((studio) => studio.id === currentStudioId)
          ? currentStudioId
          : this.studios[0]?.id || null;
      } catch (error) {
        console.error('Unable to load studios for the global selector:', error?.message || error);
      } finally {
        this.loadingStudios = false;
      }
    },
    async loadPendingCounts() {
      try {
        const { data } = await secureAxios.get('/api/card-expenses/pending-counts');
        this.pendingCountsByStudio = (data || []).reduce((counts, row) => {
          counts[Number(row.studioId)] = Number(row.pendingCount || 0);
          return counts;
        }, {});
      } catch (error) {
        console.error('Unable to load pending Card Expense counts:', error?.message || error);
      }
    },
    async loadUnlinkedBankCounts() {
      if (!this.canSwitchStudio) return;
      try {
        const { data } = await secureAxios.get('/api/models/bank-link-counts');
        this.unlinkedBankCountsByStudio = (data || []).reduce((counts, row) => {
          counts[Number(row.studioId)] = Number(row.unlinkedBankCount || 0);
          return counts;
        }, {});
      } catch (error) {
        console.error('Unable to load unlinked bank account counts:', error?.message || error);
      }
    },
    refreshHeaderAlerts() {
      this.loadPendingCounts();
      this.loadUnlinkedBankCounts();
    },
    pendingForStudio(studioId) {
      return Number(this.pendingCountsByStudio[Number(studioId)] || 0);
    },
    unlinkedBanksForStudio(studioId) {
      return Number(this.unlinkedBankCountsByStudio[Number(studioId)] || 0);
    },
    badgeCount(count) {
      return Number(count) > 99 ? '99+' : String(Number(count));
    },
    switchStudio(studioId) {
      const nextStudioId = Number(studioId);
      if (!Number.isInteger(nextStudioId) || nextStudioId <= 0) return;
      if (nextStudioId === Number(this.user.studio_id)) return;

      const accountStudioId = Number(this.user.account_studio_id || this.user.studio_id);
      this.user = {
        ...this.user,
        account_studio_id: accountStudioId,
        studio_id: nextStudioId,
      };
      localStorage.setItem('user', JSON.stringify(this.user));
      window.location.reload();
    },
    selectItem(item) {
      if (item.route) {
        if (this.$route.path !== item.route) this.$router.push(item.route);
        return;
      }
      this.selectedLabel = item.label;
      this.showPlannedMessage = true;
    },
    logout() {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      localStorage.removeItem('rememberedEmail');
      this.$router.push('/login');
    },
  },
};
</script>

<style scoped>
.control-header {
  border-bottom: 1px solid #dfe7f0;
}

.control-header :deep(.v-toolbar__content) {
  gap: 8px;
  padding: 0 18px 0 14px;
}

.control-header__brand {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 9px;
  min-width: 128px;
}

.control-header__logo {
  display: block;
  width: 27px;
  height: 27px;
  object-fit: contain;
}

.control-header__title {
  color: #0a2647;
  font-size: 17px;
  font-weight: 800;
  letter-spacing: 0.045em;
  line-height: 1;
  white-space: nowrap;
}

.control-header__divider {
  align-self: stretch;
  width: 1px;
  margin: 12px 4px;
  background: #e4eaf0;
}

.control-header__navigation {
  display: flex;
  align-items: center;
  align-self: stretch;
  gap: 2px;
  min-width: 0;
}

.control-header__item {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  height: 42px;
  padding: 0 11px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: #263545;
  font: inherit;
  font-size: 13px;
  font-weight: 650;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 140ms ease, color 140ms ease;
}

.control-header__item:hover,
.control-header__item:focus-visible {
  outline: none;
  background: #eef5fb;
  color: #205295;
}

.control-header__item--active {
  background: #e6f1fa;
  color: #144272;
}

.control-header__mobile-menu {
  display: none;
  color: #205295;
}

.control-header__studio-switcher {
  display: grid;
  grid-template-columns: auto 88px;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
}

.control-header__studio-label {
  color: #66788a;
  font-size: 11px;
  font-weight: 750;
  letter-spacing: 0.055em;
  text-transform: uppercase;
}

.control-header__studio-select {
  width: 88px;
}

.control-header__studio-select-wrap {
  position: relative;
  width: 88px;
}

.control-header__pending-badge,
.control-header__selector-alert {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: #d9363e;
  color: #fff;
  font-size: 10px;
  font-weight: 800;
  line-height: 1;
}

.control-header__bank-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-width: 22px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: #d58b1f;
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  line-height: 1;
  white-space: nowrap;
}

.control-header__studio-alerts {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 12px;
}

.control-header__pending-badge--menu {
  margin-left: 0;
}

.control-header__bank-badge--menu {
  margin-left: 0;
}

.control-header__selector-alert {
  position: absolute;
  z-index: 2;
  top: -7px;
  right: -7px;
  border: 2px solid #fff;
  box-sizing: content-box;
}

.control-header__selector-bank-alert {
  position: absolute;
  z-index: 2;
  right: -9px;
  bottom: -9px;
  border: 2px solid #fff;
  box-sizing: content-box;
}

.control-header__studio-select :deep(.v-field) {
  min-height: 34px;
  font-size: 12px;
  font-weight: 700;
}

.control-header__studio-select :deep(.v-field__input) {
  min-height: 34px;
  padding-top: 5px;
  padding-bottom: 5px;
}

.control-header__sidebar-toggle {
  display: none;
  color: #205295;
}

@media (max-width: 1180px) {
  .control-header__item {
    gap: 5px;
    padding: 0 8px;
    font-size: 12px;
  }
}

@media (max-width: 980px) {
  .control-header__navigation,
  .control-header__divider {
    display: none;
  }

  .control-header__mobile-menu {
    display: inline-flex;
  }

  .control-header__sidebar-toggle {
    display: inline-flex;
  }
}

@media (max-width: 420px) {
  .control-header :deep(.v-toolbar__content) {
    padding-left: 10px;
    padding-right: 8px;
  }

  .control-header__brand {
    min-width: 0;
  }

  .control-header__studio-switcher {
    grid-template-columns: 76px;
  }

  .control-header__studio-label {
    display: none;
  }

  .control-header__studio-select {
    width: 76px;
  }

  .control-header__studio-select-wrap {
    width: 76px;
  }
}
</style>
